// Pure Knowledge Base helpers extracted from KnowledgeBase.tsx, BulkUpload.tsx
// and SimilarityCheckStep.tsx so the logic is unit-testable without the UI.

export type SimilarityConfidenceLevel = 'high' | 'medium' | 'low';

const EXCEL_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
];

export interface BulkUploadFileValidation {
  isValid: boolean;
  errorMessage: string | null;
}

/**
 * Validate a bulk-upload file: must be an Excel file by MIME type or extension.
 */
export function validateBulkUploadFile(file: Pick<File, 'name' | 'type'>): BulkUploadFileValidation {
  const hasExcelMimeType = EXCEL_MIME_TYPES.includes(file.type);
  const hasExcelExtension = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

  if (!hasExcelMimeType && !hasExcelExtension) {
    return { isValid: false, errorMessage: 'Please upload an Excel file (.xlsx or .xls)' };
  }
  return { isValid: true, errorMessage: null };
}

export interface MergedVariationsResult {
  merged: string[];
  addedCount: number;
}

/**
 * Merge AI-generated trigger variations into the existing list, skipping
 * duplicates and the trigger itself (case-insensitive). New entries are
 * stored lowercased, matching how existing variations are persisted.
 */
export function mergeGeneratedVariations(
  existingVariations: string[],
  generatedVariations: string[],
  trigger: string
): MergedVariationsResult {
  const newVariations = generatedVariations.filter(
    (variation) =>
      !existingVariations.includes(variation.toLowerCase()) &&
      variation.toLowerCase() !== trigger.toLowerCase()
  );
  return {
    merged: [...existingVariations, ...newVariations.map((variation) => variation.toLowerCase())],
    addedCount: newVariations.length,
  };
}

/**
 * Detect a Mongo duplicate-key error from a backend error string.
 */
export function isDuplicateKeyError(errorText: string): boolean {
  return (
    errorText.includes('E11000') ||
    errorText.includes('duplicate key') ||
    errorText.includes('already exists')
  );
}

/**
 * Human message for a similarity match, e.g. "85% match - This appears to be a duplicate".
 */
export function getSimilarityConfidenceMessage(level: string, similarity: number): string {
  const percentage = Math.round(similarity * 100);
  switch (level) {
    case 'high':
      return `${percentage}% match - This appears to be a duplicate`;
    case 'medium':
      return `${percentage}% match - This might be a variation`;
    case 'low':
      return `${percentage}% match - Related but different`;
    default:
      return `${percentage}% match`;
  }
}

export interface ConfidenceGroups<T> {
  high: T[];
  medium: T[];
  low: T[];
}

/**
 * Split similarity matches into high/medium/low confidence buckets,
 * preserving input order within each bucket.
 */
export function groupByConfidenceLevel<T extends { confidenceLevel: SimilarityConfidenceLevel }>(
  items: T[]
): ConfidenceGroups<T> {
  return {
    high: items.filter((item) => item.confidenceLevel === 'high'),
    medium: items.filter((item) => item.confidenceLevel === 'medium'),
    low: items.filter((item) => item.confidenceLevel === 'low'),
  };
}
